/**
 * This uses the password as key to encrypt a fixed plaintext string.
 * This makes it impossible to decrypt the password.
 */
export function useCrypto() {
  const plaintext = "This is a plaintext string";

  function strToArrayBuffer(str: string): Uint8Array {
    return new TextEncoder().encode(str);
    // the reverse would be new TextDecoder().decode(buffer);
  }

  async function deriveKey(password: string) {
    const passwordKey = await crypto.subtle.importKey(
      "raw",
      strToArrayBuffer(password),
      { name: "PBKDF2" },
      false,
      ["deriveKey"],
    );

    // Fixed salt for simplicity; use a random one in production
    const salt = new Uint8Array(16);

    // Generate a derived key with PBKDF2
    return await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt,
        iterations: 100000,
        hash: "SHA-256",
      },
      passwordKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  }

  // Encrypt the constant string with the key derived from the
  // password and the initialization value
  async function encrypt(
    password: string,
    maybeInitializationValue?: Uint8Array,
  ) {
    const iv =
      maybeInitializationValue ?? crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(password);
    const ciphertext: ArrayBuffer = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      strToArrayBuffer(plaintext),
    );

    return { ciphertext: new Uint8Array(ciphertext), iv };
  }

  function arraysAreEqual(arr1: Uint8Array, arr2: Uint8Array) {
    return (
      arr1.length === arr2.length &&
      arr1.every((value, index) => value === arr2[index])
    );
  }

  return { encrypt, arraysAreEqual };
}
